import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { personHref } from '../utils/prettyArchiveLinks';
import { toast } from 'sonner';
import { 
  Dna,
  User,
  ChevronDown,
  ChevronUp,
  Eye,
  BookmarkPlus,
  Loader2,
  Shield,
  MapPin,
  GitBranch,
  Info,
  Sparkles,
  ExternalLink,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confidence tier styling and labels - calm tones with design system colors
const confidenceTierConfig = {
  strong: {
    label: 'Strong',
    description: 'Shared ancestral signals strongly detected',
    barWidth: '100%',
    barColor: 'bg-primary'
  },
  moderate: {
    label: 'Moderate',
    description: 'Notable ancestral patterns observed',
    barWidth: '66%',
    barColor: 'bg-primary/80'
  },
  tentative: {
    label: 'Tentative',
    description: 'Possible connection worth investigating',
    barWidth: '33%',
    barColor: 'bg-muted-foreground/60'
  }
};

// Relationship likelihood labels - calm, non-deterministic language
const relationshipLabels = {
  possible_close_family_line: {
    label: 'Possible Close Family Connection',
    description: 'Shared ancestral signals suggest a closer family connection may exist. This match may be worth investigating.'
  },
  possible_extended_cousin_line: {
    label: 'Possible Cousin Connection',
    description: 'Heritage patterns indicate a potential extended family relationship. Consider exploring shared ancestors.'
  },
  possible_distant_ancestral_overlap: {
    label: 'Possible Distant Ancestry',
    description: 'Some shared ancestry may exist in earlier generations. Further research recommended.'
  },
  undetermined_connection: {
    label: 'Possible DNA Connection',
    description: 'Ancestral signals detected. Further research needed to understand this connection.'
  }
};

// Confidence indicator component
const ConfidenceIndicator = ({ tier }) => {
  const config = confidenceTierConfig[tier] || confidenceTierConfig.tentative;
  
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Match Strength</span>
        <span className="text-xs text-foreground font-medium">{config.label}</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div 
          className={`h-full ${config.barColor} rounded-full transition-all duration-500`}
          style={{ width: config.barWidth }}
        />
      </div>
      <p className="text-[10px] text-muted-foreground">{config.description}</p>
    </div>
  );
};

// Shared factors display
const SharedFactors = ({ factors, detailed = false }) => {
  if (!factors || Object.keys(factors).length === 0) return null;
  
  const factorItems = [];
  
  // Haplogroup factors - genetic ancestry signals
  if (factors.maternal_haplogroup) {
    factorItems.push({ 
      icon: <Dna className="w-3 h-3" />, 
      label: 'Maternal Lineage', 
      value: factors.maternal_haplogroup,
      detail: 'Shared maternal ancestral line'
    });
  }
  if (factors.maternal_haplogroup_branch) {
    factorItems.push({ 
      icon: <Dna className="w-3 h-3" />, 
      label: 'Maternal Branch', 
      value: factors.maternal_haplogroup_branch,
      detail: 'Similar maternal haplogroup branch'
    });
  }
  if (factors.paternal_haplogroup) {
    factorItems.push({ 
      icon: <Dna className="w-3 h-3" />, 
      label: 'Paternal Lineage', 
      value: factors.paternal_haplogroup,
      detail: 'Shared paternal ancestral line'
    });
  }
  if (factors.paternal_haplogroup_branch) {
    factorItems.push({ 
      icon: <Dna className="w-3 h-3" />, 
      label: 'Paternal Branch', 
      value: factors.paternal_haplogroup_branch,
      detail: 'Similar paternal haplogroup branch'
    });
  }
  
  // Ethnicity regions - geographic heritage overlap
  if (factors.ethnicity_regions && factors.ethnicity_regions.length > 0) {
    const regions = factors.ethnicity_regions.map(r => r.region).join(', ');
    factorItems.push({ 
      icon: <MapPin className="w-3 h-3" />, 
      label: 'Heritage Regions', 
      value: regions,
      detail: 'Overlapping geographic ancestry estimates',
      ethnicityData: factors.ethnicity_regions
    });
  }
  
  // Tree context - surname, year, location overlap
  if (factors.shared_surname) {
    factorItems.push({ 
      icon: <User className="w-3 h-3" />, 
      label: 'Surname Match', 
      value: factors.shared_surname,
      detail: 'Same family surname in linked tree records'
    });
  }
  if (factors.birth_years_close) {
    factorItems.push({ 
      icon: <User className="w-3 h-3" />, 
      label: 'Generation Overlap', 
      value: 'Same generation',
      detail: 'Birth years within 5 years suggest same generation'
    });
  }
  if (factors.birth_years_same_generation) {
    factorItems.push({ 
      icon: <User className="w-3 h-3" />, 
      label: 'Similar Era', 
      value: 'Within 20 years',
      detail: 'Birth years within same generational window'
    });
  }
  if (factors.shared_location_terms && factors.shared_location_terms.length > 0) {
    factorItems.push({ 
      icon: <MapPin className="w-3 h-3" />, 
      label: 'Geographic Link', 
      value: factors.shared_location_terms.join(', '),
      detail: 'Overlapping locations in family records'
    });
  }
  
  if (factorItems.length === 0) return null;
  
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Shared Ancestral Signals</p>
      <div className="space-y-1.5">
        {factorItems.map((item, idx) => (
          <div key={idx} className={`flex items-start gap-2 ${detailed ? 'p-2.5 rounded-lg bg-secondary/50 border border-border' : ''}`}>
            <span className="text-primary mt-0.5">{item.icon}</span>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground font-medium">{item.label}:</span>
                <span className="text-foreground font-medium">{item.value}</span>
              </div>
              {detailed && item.detail && (
                <p className="text-[10px] text-muted-foreground mt-0.5">{item.detail}</p>
              )}
              {detailed && item.ethnicityData && (
                <div className="mt-2 space-y-1">
                  {item.ethnicityData.slice(0, 3).map((r, i) => (
                    <div key={i} className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground">{r.region}</span>
                      <span className="text-muted-foreground">
                        You: {r.your_estimate}% | Match: {r.match_estimate}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Single match card
const DNAMatchCard = ({ match, trees, onViewContext, onSaveToJournal, isSaving }) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [selectedTreeId, setSelectedTreeId] = useState('');
  const [notes, setNotes] = useState('');
  
  const relationship = relationshipLabels[match.relationship_likelihood] || relationshipLabels.undetermined_connection;
  
  const handleSave = () => {
    if (!selectedTreeId) {
      toast.error('Please select a tree');
      return;
    }
    onSaveToJournal(match.match_id, selectedTreeId, notes);
    setShowSaveDialog(false);
    setNotes('');
  };
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <Card className="bg-card border-border hover:border-primary/30 transition-colors shadow-sm" data-testid={`dna-match-card-${match.match_id}`}>
          <CardContent className="p-4 space-y-4">
            {/* Header with person names */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                <Dna className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {match.person_name ? (
                    <span className="text-sm font-medium text-foreground">{match.person_name}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">Your DNA Profile</span>
                  )}
                  <GitBranch className="w-3 h-3 text-muted-foreground" />
                  {match.matched_person_name ? (
                    <span className="text-sm font-medium text-foreground">{match.matched_person_name}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">Another Researcher</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{relationship.label}</p>
              </div>
              <div className="text-right">
                <span className="text-lg font-mono text-foreground font-semibold">{match.dna_score}</span>
                <p className="text-[10px] text-muted-foreground">score</p>
              </div>
            </div>
            
            {/* Relationship description */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/60 border border-border">
              <Shield className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground leading-relaxed italic">
                {relationship.description}
              </p>
            </div>
            
            {/* Shared factors */}
            <SharedFactors factors={match.shared_factors} />
            
            {/* Confidence indicator */}
            <ConfidenceIndicator tier={match.confidence_tier} />
            
            {/* Action buttons */}
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewContext(match)}
                className="flex-1 h-9 text-xs border-border text-foreground hover:bg-secondary hover:text-foreground"
                data-testid={`view-context-btn-${match.match_id}`}
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                View Match Context
              </Button>
              <Button
                size="sm"
                onClick={() => setShowSaveDialog(true)}
                disabled={isSaving}
                className="flex-1 h-9 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid={`save-journal-btn-${match.match_id}`}
              >
                <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
                Save to Journal
              </Button>
            </div>
            
            {/* Privacy mode indicator */}
            {match.privacy_mode === 'restricted' && (
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <Shield className="w-3 h-3" />
                <span>Limited details • Privacy protected</span>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
      
      {/* Save to Research Journal Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-serif">Save to Research Journal</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add this DNA match to your Research Journal for further investigation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Match summary */}
            <div className="p-3 rounded-lg bg-secondary border border-border">
              <div className="flex items-center gap-2 mb-1">
                <Dna className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{relationship.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Score: {match.dna_score} • {confidenceTierConfig[match.confidence_tier]?.label || 'Tentative'} confidence
              </p>
            </div>
            
            {/* Tree selector */}
            <div className="space-y-2">
              <Label className="text-sm text-foreground font-medium">Select Tree</Label>
              <Select value={selectedTreeId} onValueChange={setSelectedTreeId}>
                <SelectTrigger className="bg-background border-input text-foreground" data-testid="save-tree-select">
                  <SelectValue placeholder="Choose a tree..." />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {trees.map((tree) => (
                    <SelectItem key={tree.id} value={tree.id} className="text-popover-foreground">
                      {tree.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm text-foreground font-medium">Research Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any initial thoughts or research questions..."
                className="bg-background border-input text-foreground min-h-[80px] text-sm placeholder:text-muted-foreground"
                data-testid="save-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowSaveDialog(false)}
              className="border-border text-foreground hover:bg-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!selectedTreeId || isSaving}
              className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              data-testid="confirm-save-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <BookmarkPlus className="w-4 h-4 mr-2" />
              )}
              Save to Journal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// View Match Context Dialog
const ViewContextDialog = ({ match, open, onClose }) => {
  const navigate = useNavigate();
  
  if (!match) return null;
  
  const relationship = relationshipLabels[match.relationship_likelihood] || relationshipLabels.undetermined_connection;
  const config = confidenceTierConfig[match.confidence_tier] || confidenceTierConfig.tentative;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2 font-serif">
            <Dna className="w-5 h-5 text-primary" />
            Possible DNA Connection
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Understanding why this match was suggested
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-5">
          {/* Connection overview */}
          <div className="p-4 rounded-lg bg-secondary/60 border border-border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Ancestral Connection</span>
              <span className="text-xs font-mono text-foreground font-medium">Match Score: {match.dna_score}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 text-center p-3 rounded-lg bg-background border border-border">
                <p className="text-sm font-medium text-foreground">{match.person_name || 'Your Profile'}</p>
                <p className="text-[10px] text-muted-foreground">Your family tree</p>
              </div>
              <GitBranch className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 text-center p-3 rounded-lg bg-background border border-border">
                <p className="text-sm font-medium text-foreground">{match.matched_person_name || 'Another Researcher'}</p>
                <p className="text-[10px] text-muted-foreground">Potential connection</p>
              </div>
            </div>
          </div>
          
          {/* Why this match was suggested */}
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Why This Match Was Suggested</p>
            <div className="p-3 rounded-lg bg-secondary/50 border border-border">
              <p className="text-sm font-medium text-foreground">{relationship.label}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{relationship.description}</p>
            </div>
          </div>
          
          {/* Confidence */}
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Match Confidence</p>
            <ConfidenceIndicator tier={match.confidence_tier} />
          </div>
          
          {/* Shared factors detailed breakdown */}
          {match.shared_factors && Object.keys(match.shared_factors).length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Shared Ancestral Signals Breakdown</p>
              <SharedFactors factors={match.shared_factors} detailed={true} />
            </div>
          )}
          
          {/* Research guidance */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
            <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-foreground font-medium">This Match May Be Worth Investigating</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                DNA connections are genealogical leads to explore, not confirmed relationships. 
                Consider comparing family trees, geographic origins, and historical records 
                to understand this potential ancestral link.
              </p>
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="space-y-2">
            {match.person_id && (
              <Button
                variant="outline"
                className="w-full border-border text-foreground hover:bg-secondary"
                onClick={() => {
                  navigate(personHref({ id: match.person_id, slug: match.person_slug }));
                  onClose(false);
                }}
                data-testid="view-person-btn"
              >
                <User className="w-4 h-4 mr-2" />
                View {match.person_name || 'Person'} Profile
              </Button>
            )}
            {match.person_id && (
              <Button
                variant="outline"
                className="w-full border-border text-foreground hover:bg-secondary"
                onClick={() => {
                  const treeId = match.tree_id || '431c2096-1f0d-4dfb-8834-015e6c4aa99d';
                  navigate(`/timeline/${treeId}?highlight=${match.person_id}`);
                  onClose(false);
                }}
                data-testid="view-timeline-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View in Family Timeline
              </Button>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="border-border text-foreground hover:bg-secondary">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main DNAMatchesPanel component
const DNAMatchesPanel = ({ profileId = null }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [profilesCount, setProfilesCount] = useState(0);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('loading');
  const [trees, setTrees] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [viewingMatch, setViewingMatch] = useState(null);
  
  // Fetch matches
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch matches
        const params = new URLSearchParams();
        if (profileId) params.append('profile_id', profileId);
        
        const response = await axios.get(`${API}/dna/matches?${params.toString()}`);
        
        setMatches(response.data.matches || []);
        setTotalMatches(response.data.total_matches || 0);
        setProfilesCount(response.data.your_profiles_count || 0);
        setMessage(response.data.message || '');
        setStatus(response.data.status || 'success');
        
        // Fetch trees for save dialog
        const treesResponse = await axios.get(`${API}/trees`);
        setTrees(treesResponse.data || []);
      } catch (error) {
        console.error('Error fetching DNA matches:', error);
        setStatus('error');
        setMessage('Unable to load DNA matches at this time');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [profileId]);
  
  const handleViewContext = (match) => {
    setViewingMatch(match);
  };
  
  const handleSaveToJournal = async (matchId, treeId, notes) => {
    setIsSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('tree_id', treeId);
      if (notes) params.append('notes', notes);
      
      const response = await axios.post(`${API}/dna/matches/${matchId}/save-investigation?${params.toString()}`);
      
      if (response.data.status === 'already_saved') {
        toast.info('This match is already in your Research Journal');
      } else {
        toast.success('DNA match saved to Research Journal', { duration: 3000 });
      }
    } catch (error) {
      console.error('Error saving to journal:', error);
      toast.error('Unable to save to Research Journal');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <>
      <Card className="bg-card border-border shadow-sm" data-testid="dna-matches-panel">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="p-4 pb-2">
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-secondary">
                    <Dna className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-medium text-foreground">DNA Connections</h3>
                    <p className="text-xs text-muted-foreground">Possible ancestral matches</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {totalMatches > 0 && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                      {totalMatches} {totalMatches === 1 ? 'possible match' : 'possible matches'}
                    </span>
                  )}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          
          <CollapsibleContent>
            <CardContent className="p-4 pt-2 space-y-4">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-primary animate-spin mb-2" />
                  <p className="text-sm text-muted-foreground">Searching for ancestral connections...</p>
                </div>
              ) : status === 'no_eligible_profiles' ? (
                <div className="text-center py-6 space-y-3">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto">
                    <Shield className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{message}</p>
                  <p className="text-xs text-muted-foreground">
                    DNA matching requires explicit consent and is always under your control.
                  </p>
                </div>
              ) : matches.length > 0 ? (
                <div className="space-y-4">
                  {/* Info message */}
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <p className="text-[11px] text-foreground leading-relaxed">
                      Shared ancestral signals detected. These matches may be worth investigating as potential family connections.
                    </p>
                  </div>
                  
                  {/* Matches list */}
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                    <AnimatePresence>
                      {matches.map((match) => (
                        <DNAMatchCard
                          key={match.match_id}
                          match={match}
                          trees={trees}
                          onViewContext={handleViewContext}
                          onSaveToJournal={handleSaveToJournal}
                          isSaving={isSaving}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                  
                  {/* Privacy notice */}
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50 border border-border">
                    <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                      Privacy protected. Matches are based on shared heritage markers only. 
                      No private details or confirmed relationships are implied.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 space-y-3">
                  <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto">
                    <Sparkles className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-foreground font-medium">No DNA matches found yet.</p>
                    <p className="text-xs text-muted-foreground max-w-[280px] mx-auto leading-relaxed">
                      As more consented profiles are added, possible ancestral connections may appear.
                    </p>
                  </div>
                  {profilesCount > 0 && (
                    <div className="flex items-center justify-center gap-1.5 text-[10px] text-primary pt-2 font-medium">
                      <CheckCircle className="w-3 h-3" />
                      <span>{profilesCount} {profilesCount === 1 ? 'profile' : 'profiles'} ready for matching</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
      
      {/* View Context Dialog */}
      <ViewContextDialog
        match={viewingMatch}
        open={!!viewingMatch}
        onClose={() => setViewingMatch(null)}
      />
    </>
  );
};

export default DNAMatchesPanel;
